import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FuzzyActions } from 'actions';
import { getEntityCustomization } from 'actions/Fuzzy';
import createCustomOption from './components/FormatOptionLabel';

import Select from './Select';

const propTypes = {
    autoFocus: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    hint: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    onChange: PropTypes.func,
    value: PropTypes.any,
    className: PropTypes.string,
    error: PropTypes.any,
    mandatory: PropTypes.bool,
    readOnly: PropTypes.bool,
    hidden: PropTypes.bool,
    list: PropTypes.string,
    description: PropTypes.string,
    loadOptions: PropTypes.func,
    environmentField: PropTypes.object,
    idEnvironment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customOption: PropTypes.func,
    excludeDisabledStates: PropTypes.bool,
};

const SelectFuzzy = memo(
    ({
        shouldRenderField,
        hidden,
        options,
        list,
        onFetchOptions,
        field,
        feature,
        parentField,
        parentFieldForBackend,
        parentValue,
        isPureDependency,
        environmentField,
        idEnvironment,
        excludeDisabledStates,
        value,
        customOption: customOptionProp,
        ...props
    }) => {
        const dispatch = useDispatch();

        // Enrich value with media (avatar) if value is an object
        // For primitive values, Select will handle via usePlainValue
        const enrichedValue = useMemo(() => {
            if (!value) return undefined;
            // If primitive ID, pass through - Select will use usePlainValue
            if (typeof value !== 'object' || Array.isArray(value)) return value;
            if (value.src) return value;
            return {
                ...value,
                ...getEntityCustomization(value, list),
            };
        }, [value, list]);

        const customOption = useMemo(
            () => createCustomOption(list, customOptionProp),
            [list, customOptionProp],
        );

        const fetchOptions = useCallback(
            (text) => {
                const searchText = text ?? '';
                if (onFetchOptions) {
                    return onFetchOptions({
                        list,
                        field,
                        text: searchText,
                        feature,
                    });
                } else {
                    // there are two types of dependency here... the ones standard for backend,
                    // who expect for parentField and parentValue (and an extrafieldunit in
                    // whereInfo field of the final XML), and the new available dependency
                    // sending to backend an "IdParent" field inside the same whereInfo
                    // used to filter by the dependant field, which seems that is calculated
                    // by backend in time of processing the request
                    let finalParentField = parentFieldForBackend || parentField;
                    let finalParentValue = parentValue;
                    let finalIdParent;
                    if (isPureDependency) {
                        finalIdParent = parentValue?.value || parentValue;
                        finalParentField = null;
                        finalParentValue = null;
                    }

                    // Normalize environmentId - could be object { value, label } or primitive
                    const envValue = environmentField?.value;
                    const envId =
                        envValue && typeof envValue === 'object'
                            ? envValue.value || envValue.id
                            : envValue;

                    return dispatch(
                        FuzzyActions.getFuzzy({
                            list,
                            field,
                            text: searchText,
                            feature,
                            parentField: finalParentField,
                            parentValue: finalParentValue,
                            idParent: finalIdParent,
                            environmentId: envId || idEnvironment,
                            excludeDisabledStates,
                        }),
                    );
                }
            },
            [
                dispatch,
                onFetchOptions,
                list,
                field,
                feature,
                parentField,
                parentFieldForBackend,
                parentValue,
                isPureDependency,
                environmentField,
                idEnvironment,
                excludeDisabledStates,
            ],
        );

        if (hidden) return null;
        if (shouldRenderField && !shouldRenderField(props?.fieldId, list)) return null;

        return (
            <Select
                className="fm-field-server-list"
                loadOptions={list ? fetchOptions : null}
                options={options}
                list={list}
                value={enrichedValue}
                customOption={customOption}
                alwaysLoadOnFocus
                {...props}
                parentValue={parentValue}
                isFuzzy
            />
        );
    },
);

SelectFuzzy.displayName = 'SelectFuzzy';

SelectFuzzy.propTypes = propTypes;

export default SelectFuzzy;
